// ==============================================
// Admin → App Library — third-party apps curated for the Scout.
//
// One table: every app, its released version, the newest uploaded version and
// its state, who it is offered to, and how much of the fleet runs the released
// build. Selecting a row opens the app: version history, release controls,
// plan gating, and "Add version" — drop an APK set (base + splits), the browser
// reads the manifest and hashes each file, then streams the files to R2 in
// parts. A new version starts in Review; Testing puts it on Tester Scouts;
// Released puts it on every plan it is offered to. "Add app" starts from the
// APK set too (AppLibraryAddApp.tsx): the files fill the form.
// ==============================================
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowPathIcon, ArrowUpTrayIcon, BarsArrowDownIcon, BarsArrowUpIcon, CheckCircleIcon, ExclamationTriangleIcon, PlusIcon, TrashIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { readApkManifest, sha256File, type ApkManifestInfo } from '../../lib/apkManifest';
import { AddAppModal } from './AppLibraryAddApp';
import {
  SHELVES, errText, findStuckDraft, mb, progressText, resumeApkSetVersion, sameFilesAsDraft, shelfOf, uploadApkSetVersion,
  type Availability, type UploadProgress,
} from './appLibraryShared';

type State = 'draft' | 'review' | 'testing' | 'released' | 'archived';
interface ApkFile { name: string; key: string | null; sha256: string; size: number; uploaded: boolean }
interface Version {
  id: string; appId: string; versionName: string; versionCode: number; state: State; files: ApkFile[]; totalBytes: number;
  note: string | null; required: boolean; uploadedBy: string | null; createdAt: string; releasedAt: string | null; archivedAt: string | null;
}
interface Fleet { on_released: number; behind: number; missing: number; total_scouts: number; failing: number; by_version: Array<{ version_code: number | null; devices: number }> }

function ShelfChip({ availability }: { availability: Availability }) {
  const tone = availability === 'skipper' ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/60'
    : availability === 'admin_only' ? 'bg-amber-900/30 text-amber-300 border-amber-700/60'
    : 'bg-slate-700/50 text-slate-400 border-slate-600';
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${tone}`}>{shelfOf(availability).label}</span>;
}

interface App {
  id: string; packageName: string; name: string; vendor: string | null; description: string | null;
  distribution: 'library' | 'skipper_managed'; offeredPackageIds: string[]; region: string | null;
  availability: Availability; tileGraphic: string | null; versions: Version[]; fleet: Fleet;
}
interface Plan { id: string; name: string; visibility: string | null }

const STATE_STYLE: Record<State, string> = {
  draft: 'bg-slate-700/60 text-slate-300',
  review: 'bg-amber-900/40 text-amber-300',
  testing: 'bg-cyan-900/40 text-cyan-300',
  released: 'bg-emerald-900/40 text-emerald-300',
  archived: 'bg-slate-800 text-slate-500',
};
const STATE_LABEL: Record<State, string> = { draft: 'Uploading', review: 'Review', testing: 'Testing', released: 'Released', archived: 'Archived' };

const released = (a: App) => a.versions.find((v) => v.state === 'released') ?? null;
const newest = (a: App) => [...a.versions].filter((v) => v.state !== 'archived').sort((x, y) => y.versionCode - x.versionCode)[0] ?? null;
const attention = (a: App) => (a.versions.some((v) => v.state === 'review') ? 0 : a.versions.some((v) => v.state === 'testing') ? 1 : a.versions.some((v) => v.state === 'draft') ? 2 : 3);

function StateChip({ state }: { state: State }) {
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${STATE_STYLE[state]}`}>{STATE_LABEL[state]}</span>;
}

function FleetBar({ fleet }: { fleet: Fleet }) {
  const pct = fleet.total_scouts > 0 ? Math.round((fleet.on_released / fleet.total_scouts) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[150px]">
      <div className="flex-1 h-2 rounded bg-slate-700 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
      <span className="text-xs text-slate-400 whitespace-nowrap tabular-nums">{fleet.on_released} / {fleet.total_scouts}</span>
    </div>
  );
}

// ---- Add version (drop an APK set) ----
interface Picked { file: File; manifest: ApkManifestInfo | null; sha256: string | null; error: string | null }

function AddVersionPanel({ app, onDone }: { app: App; onDone: () => void }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [reading, setReading] = useState(false);
  const [note, setNote] = useState('');
  const [required, setRequired] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [versionCode, setVersionCode] = useState('');
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Play delivers base.apk + split_config.*.apk; keep the browser's names but make the base predictable.
  const files = () => picked.map((p) => ({ name: p.manifest && !p.manifest.isSplit ? 'base.apk' : p.file.name, file: p.file, sha256: p.sha256 ?? '' }));
  // An earlier upload of this version code that stopped part way: resume it with the same files, or replace it.
  const stuck = picked.length > 0 ? findStuckDraft(app.versions, Number(versionCode) || null) : null;
  const resumable = !!stuck && picked.every((p) => p.sha256) && sameFilesAsDraft(stuck, files());
  const base = picked.find((p) => p.manifest && !p.manifest.isSplit) ?? null;
  const packageMismatch = picked.some((p) => p.manifest && p.manifest.packageName && p.manifest.packageName !== app.packageName);
  const total = picked.reduce((n, p) => n + p.file.size, 0);
  const cur = newest(app);

  const onFiles = async (list: FileList | File[]) => {
    setError(null); setReading(true);
    const out: Picked[] = [];
    for (const file of Array.from(list)) {
      if (!file.name.toLowerCase().endsWith('.apk')) continue;
      try {
        const [manifest, sha] = await Promise.all([readApkManifest(file).catch(() => null), sha256File(file)]);
        out.push({ file, manifest, sha256: sha, error: null });
      } catch (e: any) {
        out.push({ file, manifest: null, sha256: null, error: e?.message ?? 'Could not read this file' });
      }
    }
    setPicked(out);
    const b = out.find((p) => p.manifest && !p.manifest.isSplit)?.manifest;
    if (b) { setVersionName(b.versionName ?? ''); setVersionCode(String(b.versionCode)); }
    setReading(false);
  };

  const upload = useMutation({
    mutationFn: async () => {
      if (stuck && resumable) {
        if (note.trim() || required) await api.adminAppLibraryPatchVersion(stuck.id, { note: note.trim() || null, required });
        await resumeApkSetVersion(stuck, files(), setProgress);
        return;
      }
      if (stuck) await api.adminAppLibraryDeleteVersion(stuck.id);
      await uploadApkSetVersion(app.id, { versionName: versionName.trim(), versionCode: Number(versionCode), note: note.trim() || null, required }, files(), setProgress);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'app-library'] }); onDone(); },
    onError: (e: any) => { setProgress(null); setError(errText(e, 'Upload failed')); qc.invalidateQueries({ queryKey: ['admin', 'app-library'] }); },
  });

  const ready = picked.length > 0 && !!base && !packageMismatch && picked.every((p) => p.sha256) && versionName.trim() && Number(versionCode) > 0 && !reading;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4 space-y-3">
      <div className="flex items-center justify-between"><h4 className="font-semibold text-white">Add a version</h4><button onClick={onDone} className="text-slate-400 hover:text-white text-sm">Cancel</button></div>
      <div
        className="rounded-lg border-2 border-dashed border-slate-600 p-5 text-center text-sm text-slate-400 hover:border-cyan-500 cursor-pointer"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); void onFiles(e.dataTransfer.files); }}
      >
        <ArrowUpTrayIcon className="mx-auto h-6 w-6 mb-1" />
        Drop the APK set here, or click to choose<br />
        <span className="text-xs">base.apk plus its split_config files, exactly as pulled from a Play Store install. The browser reads the package and version and hashes each file.</span>
        <input ref={inputRef} type="file" accept=".apk" multiple aria-label="Choose the APK files for this version" className="hidden" onChange={(e) => e.target.files && void onFiles(e.target.files)} />
      </div>
      {reading && <div className="text-sm text-slate-400 flex items-center gap-2"><ArrowPathIcon className="h-4 w-4 animate-spin" /> Reading and hashing…</div>}
      {picked.length > 0 && (
        <ul className="text-sm divide-y divide-slate-800 rounded-md border border-slate-800">
          {picked.map((p) => (
            <li key={p.file.name} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="font-mono text-slate-200 truncate">{p.manifest && !p.manifest.isSplit ? 'base.apk' : p.file.name}</div>
                <div className="text-xs text-slate-500 font-mono truncate">{p.manifest ? `${p.manifest.packageName} · ${p.manifest.isSplit ? `split ${p.manifest.splitName}` : `versionCode ${p.manifest.versionCode}`}` : (p.error ?? 'manifest not read')} · {p.sha256?.slice(0, 12) ?? '…'}</div>
              </div>
              <span className="text-xs text-slate-400 whitespace-nowrap">{mb(p.file.size)}</span>
            </li>
          ))}
        </ul>
      )}
      {picked.length > 0 && !base && <div className="text-sm text-amber-300 flex items-center gap-2"><ExclamationTriangleIcon className="h-4 w-4" /> No base.apk in the set — the file whose manifest has no split name.</div>}
      {packageMismatch && <div className="text-sm text-red-300 flex items-center gap-2"><ExclamationTriangleIcon className="h-4 w-4" /> One of these files belongs to a different package than {app.packageName}.</div>}
      <div className="grid grid-cols-3 gap-3">
        <label className="block text-sm text-slate-300">Version name<input className="mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white font-mono" value={versionName} onChange={(e) => setVersionName(e.target.value)} /></label>
        <label className="block text-sm text-slate-300">Version code<input className="mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white font-mono" value={versionCode} onChange={(e) => setVersionCode(e.target.value.replace(/\D/g, ''))} /></label>
        <div className="text-sm text-slate-400 self-end pb-2">{cur ? <>Current: <span className="font-mono text-slate-200">{cur.versionName} ({cur.versionCode})</span></> : 'First version'}{total > 0 && <> · set {mb(total)}</>}</div>
      </div>
      <label className="block text-sm text-slate-300">Note shown on the Scout, one line<input className="mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white" maxLength={300} placeholder="Adds the new 7-day swell model. Sign in again after updating." value={note} onChange={(e) => setNote(e.target.value)} /></label>
      <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} className="rounded border-slate-600 bg-slate-800" /> Required update — the Launcher installs it overnight on Wi-Fi without waiting for the skipper</label>
      {stuck && resumable && <div className="text-sm text-cyan-200">The upload of {stuck.versionName} stopped part way. These are the same files, so this finishes it.</div>}
      {stuck && !resumable && <div className="text-sm text-amber-300 flex items-center gap-2"><ExclamationTriangleIcon className="h-4 w-4" /> The unfinished upload of {stuck.versionName} is not the same files as these. Replace it to start again with these files.</div>}
      {progress && <div><div className="text-sm text-slate-300 mb-1">{progressText(progress)}</div><div className="h-2 rounded bg-slate-700 overflow-hidden"><div className="h-full bg-cyan-500" style={{ width: `${progress.pct}%` }} /></div></div>}
      {error && <div className="rounded-md border border-red-800/60 bg-red-900/20 px-3 py-2 text-sm text-red-300">{error}</div>}
      <div className="flex justify-end">
        <button onClick={() => upload.mutate()} disabled={!ready || upload.isPending} className="rounded-md bg-cyan-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">{upload.isPending ? 'Uploading…' : stuck ? (resumable ? 'Resume the upload' : 'Replace the unfinished upload') : 'Upload and send for review'}</button>
      </div>
    </div>
  );
}

// ---- App detail ----
function AppDetail({ app, plans, onClose }: { app: App; plans: Plan[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'app-library'] });
  const setState = useMutation({ mutationFn: ({ id, state }: { id: string; state: State }) => api.adminAppLibrarySetVersionState(id, state), onSuccess: invalidate, onError: (e: any) => setMsg(errText(e, 'Could not change the state')) });
  const patchApp = useMutation({ mutationFn: (data: Record<string, unknown>) => api.adminAppLibraryUpdateApp(app.id, data), onSuccess: invalidate, onError: (e: any) => setMsg(errText(e, 'Could not update the app')) });
  const patchVersion = useMutation({ mutationFn: ({ id, data }: { id: string; data: { note?: string | null; required?: boolean } }) => api.adminAppLibraryPatchVersion(id, data), onSuccess: invalidate });
  const delVersion = useMutation({ mutationFn: (id: string) => api.adminAppLibraryDeleteVersion(id), onSuccess: invalidate, onError: (e: any) => setMsg(errText(e, 'Could not delete the version')) });
  const delApp = useMutation({ mutationFn: () => api.adminAppLibraryDeleteApp(app.id), onSuccess: invalidate, onError: (e: any) => setMsg(errText(e, 'Could not remove the app')) });
  const visiblePlans = plans.filter((p) => p.visibility !== 'hidden' || p.name === 'Tester');
  const versions = [...app.versions].sort((a, b) => b.versionCode - a.versionCode);

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-5 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">{app.name}</h3>
          <div className="text-xs text-slate-500 font-mono">{app.packageName}{app.vendor ? ` · ${app.vendor}` : ''}</div>
        </div>
        <div className="flex items-center gap-2">
          {app.distribution === 'library' && !adding && <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded-md bg-cyan-600 px-3 py-1.5 text-sm font-semibold text-white"><PlusIcon className="h-4 w-4" /> Add version</button>}
          <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Close"><XMarkIcon className="h-5 w-5" /></button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Offered to <span className="normal-case font-normal">(none = every plan; Tester always sees it)</span></div>
          <div className="flex flex-wrap gap-2">
            {visiblePlans.map((p) => {
              const on = app.offeredPackageIds.includes(p.id);
              return <button key={p.id} onClick={() => patchApp.mutate({ offered_package_ids: on ? app.offeredPackageIds.filter((x) => x !== p.id) : [...app.offeredPackageIds, p.id] })} className={`rounded-full px-3 py-1 text-xs font-semibold border ${on ? 'bg-cyan-600 border-cyan-500 text-white' : 'border-slate-600 text-slate-300 hover:border-slate-400'}`}>{p.name.trim()}</button>;
            })}
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">On the Scout</div>
          <div className="space-y-1">
            {SHELVES.map((sh) => (
              <label key={sh.value} className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer ${app.availability === sh.value ? 'border-cyan-500 bg-cyan-950/30' : 'border-slate-700 hover:border-slate-500'}`}>
                <input type="radio" className="mt-1" name={`shelf-${app.id}`} checked={app.availability === sh.value} onChange={() => patchApp.mutate({ availability: sh.value })} />
                <span>
                  <span className="block text-sm font-semibold text-white">{sh.label}</span>
                  <span className="block text-xs text-slate-400">{sh.blurb}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="block text-slate-300">Tile image <span className="text-slate-500">(blank = the app's own icon)</span><input className="mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white" defaultValue={app.tileGraphic ?? ''} placeholder="e.g. Navionics.png" maxLength={120} onBlur={(e) => { if ((e.target.value || null) !== (app.tileGraphic ?? null)) patchApp.mutate({ tile_graphic: e.target.value || null }); }} /></label>
          <div />
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="block text-slate-300">Distribution
            <select className="mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white" value={app.distribution} onChange={(e) => patchApp.mutate({ distribution: e.target.value })}>
              <option value="library">Vortex serves the APK</option>
              <option value="skipper_managed">Skipper-managed</option>
            </select>
          </label>
          <label className="block text-slate-300">Region<input className="mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white uppercase" defaultValue={app.region ?? ''} placeholder="Worldwide" maxLength={8} onBlur={(e) => { if ((e.target.value || null) !== (app.region ?? null)) patchApp.mutate({ region: e.target.value || null }); }} /></label>
        </div>
        <label className="block text-sm text-slate-300">What it does, one line<input className="mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white" defaultValue={app.description ?? ''} onBlur={(e) => { if ((e.target.value || null) !== (app.description ?? null)) patchApp.mutate({ description: e.target.value || null }); }} /></label>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Fleet</div>
          <FleetBar fleet={app.fleet} />
          <div className="mt-2 text-xs text-slate-400">{app.fleet.on_released} on the released version · {app.fleet.behind} behind · {app.fleet.missing} without it, of {app.fleet.total_scouts} active Scouts</div>
          {app.fleet.failing > 0 && <div className="mt-1 text-xs text-amber-300">{app.fleet.failing} Scout{app.fleet.failing === 1 ? ' cannot' : 's cannot'} install the current version (a different build is already aboard, or the install was refused)</div>}
          {app.fleet.by_version.length > 0 && <ul className="mt-2 text-xs text-slate-400 font-mono space-y-0.5">{app.fleet.by_version.map((b) => <li key={String(b.version_code)}>code {b.version_code ?? '?'} · {b.devices} Scout{b.devices === 1 ? '' : 's'}</li>)}</ul>}
        </div>
        {msg && <div className="rounded-md border border-red-800/60 bg-red-900/20 px-3 py-2 text-sm text-red-300 flex justify-between"><span>{msg}</span><button onClick={() => setMsg(null)}><XMarkIcon className="h-4 w-4" /></button></div>}
        <div className="pt-2 border-t border-slate-700/60">
          <button onClick={() => { if (confirm(`Remove ${app.name} from the library? Its archived versions and files are deleted.`)) delApp.mutate(); }} className="text-xs text-red-400 hover:text-red-300">Remove app from library</button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Version history</div>
        {adding && <AddVersionPanel app={app} onDone={() => setAdding(false)} />}
        {app.distribution === 'skipper_managed' && <div className="text-sm text-slate-400 rounded-md border border-slate-700 p-3">Skipper-managed: the Scout lists this app and reports its installed version, but Vortex does not distribute its APK. Switch to “Vortex serves the APK” once redistribution is agreed with the vendor.</div>}
        {versions.length === 0 && app.distribution === 'library' && !adding && <div className="text-sm text-slate-500">No versions yet. Add one from an APK set pulled off a Play Store install.</div>}
        <ul className="divide-y divide-slate-700/60">
          {versions.map((v) => {
            const complete = v.files.length > 0 && v.files.every((f) => f.uploaded);
            const devicesOn = app.fleet.by_version.find((b) => b.version_code === v.versionCode)?.devices ?? 0;
            return (
              <li key={v.id} className="py-3 grid grid-cols-[auto_1fr_auto] gap-3 items-start">
                <span className={`mt-1.5 h-2.5 w-2.5 rounded-full ${v.state === 'released' ? 'bg-emerald-400' : v.state === 'testing' ? 'bg-cyan-400' : v.state === 'review' ? 'bg-amber-400' : 'bg-slate-600'}`} />
                <div className="min-w-0">
                  <div className="text-sm text-white"><span className="font-mono font-semibold">{v.versionName}</span> <span className="text-slate-400">(code {v.versionCode})</span> · {v.files.length} file{v.files.length === 1 ? '' : 's'} · {mb(v.totalBytes)}{v.required && <span className="ml-2 rounded-full bg-amber-900/40 px-2 py-0.5 text-[11px] font-semibold text-amber-300">Required</span>}</div>
                  <div className="text-xs text-slate-500">{new Date(v.createdAt).toLocaleString('en-NZ')}{v.releasedAt ? ` · released ${new Date(v.releasedAt).toLocaleDateString('en-NZ')}` : ''}{devicesOn ? ` · on ${devicesOn} Scout${devicesOn === 1 ? '' : 's'}` : ''}{!complete ? ' · files still uploading' : ''}</div>
                  {v.note && <div className="text-xs text-slate-300 mt-1">“{v.note}”</div>}
                  <div className="text-[11px] text-slate-600 font-mono mt-1 truncate">{v.files.map((f) => `${f.name} ${f.sha256.slice(0, 8)}`).join(' · ')}</div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <StateChip state={v.state} />
                  <div className="flex gap-1">
                    {v.state === 'review' && complete && <button onClick={() => setState.mutate({ id: v.id, state: 'testing' })} className="rounded-md border border-cyan-700 px-2 py-1 text-xs text-cyan-300">To Tester</button>}
                    {(v.state === 'review' || v.state === 'testing') && complete && <button onClick={() => { if (confirm(`Release ${app.name} ${v.versionName} to every plan it is offered to?`)) setState.mutate({ id: v.id, state: 'released' }); }} className="rounded-md bg-emerald-700 px-2 py-1 text-xs font-semibold text-white">Release</button>}
                    {v.state === 'testing' && <button onClick={() => setState.mutate({ id: v.id, state: 'review' })} className="rounded-md border border-slate-600 px-2 py-1 text-xs text-slate-300">Pull back</button>}
                    {v.state === 'released' && <button onClick={() => { if (confirm('Archive the released version? Scouts will be offered nothing until another version is released.')) setState.mutate({ id: v.id, state: 'archived' }); }} className="rounded-md border border-slate-600 px-2 py-1 text-xs text-slate-300">Archive</button>}
                    {v.state === 'archived' && <button onClick={() => setState.mutate({ id: v.id, state: 'review' })} className="rounded-md border border-slate-600 px-2 py-1 text-xs text-slate-300">Restore to review</button>}
                    {(v.state === 'draft' || v.state === 'review' || v.state === 'archived') && <button title="Delete version and its files" onClick={() => { if (confirm(`Delete ${v.versionName} and its files?`)) delVersion.mutate(v.id); }} className="rounded-md border border-red-900/60 px-2 py-1 text-xs text-red-300"><TrashIcon className="h-3.5 w-3.5" /></button>}
                  </div>
                  {v.state !== 'archived' && <label className="flex items-center gap-1 text-[11px] text-slate-400"><input type="checkbox" checked={v.required} onChange={(e) => patchVersion.mutate({ id: v.id, data: { required: e.target.checked } })} className="rounded border-slate-600 bg-slate-800" /> required</label>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

type SortKey = 'name' | 'newest' | 'state' | 'availability' | 'fleet';

/** Rank the states the way they run, so sorting by state reads as a pipeline. */
const STATE_ORDER: Record<State, number> = { draft: 0, review: 1, testing: 2, released: 3, archived: 4 };
const SHELF_ORDER: Record<Availability, number> = { skipper: 0, admin_only: 1, withheld: 2 };

function compare(a: App, b: App, key: SortKey): number {
  switch (key) {
    case 'name': return a.name.localeCompare(b.name);
    case 'newest': return (newest(a)?.versionCode ?? -1) - (newest(b)?.versionCode ?? -1);
    case 'state': {
      const rank = (x: App) => { const st = newest(x)?.state; return st ? STATE_ORDER[st] : 9; };
      return rank(a) - rank(b);
    }
    case 'availability': return SHELF_ORDER[a.availability] - SHELF_ORDER[b.availability];
    // Share of the fleet on the released build, so "least rolled out" sorts first.
    case 'fleet': {
      const share = (x: App) => (x.fleet.total_scouts ? x.fleet.on_released / x.fleet.total_scouts : -1);
      return share(a) - share(b);
    }
  }
}

/** A column header that sorts: click for ascending, again for descending, again for the default order. */
function SortHeader({ label, k, sort, onSort, className = '' }: {
  label: string; k: SortKey; sort: { key: SortKey; dir: 'asc' | 'desc' } | null; onSort: (k: SortKey) => void; className?: string;
}) {
  const active = sort?.key === k;
  return (
    <th className={`px-3 py-2.5 text-left ${className}`}>
      <button onClick={() => onSort(k)} className={`inline-flex items-center gap-1 uppercase tracking-wider ${active ? 'text-cyan-300' : 'hover:text-slate-200'}`}>
        {label}
        {active && (sort!.dir === 'asc' ? <BarsArrowUpIcon className="h-3.5 w-3.5" /> : <BarsArrowDownIcon className="h-3.5 w-3.5" />)}
      </button>
    </th>
  );
}

// ---- Page ----
export function AdminAppLibraryPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const [fState, setFState] = useState<'all' | State | 'none'>('all');
  const [fShelf, setFShelf] = useState<'all' | Availability>('all');
  const [fDist, setFDist] = useState<'all' | 'library' | 'skipper_managed'>('all');
  const [fPlan, setFPlan] = useState<'all' | string>('all');
  const { data: apps = [], isLoading, isError, refetch } = useQuery<App[]>({ queryKey: ['admin', 'app-library'], queryFn: () => api.adminAppLibraryList(), refetchInterval: 30_000 });
  const { data: plans = [] } = useQuery<Plan[]>({ queryKey: ['admin', 'app-library', 'plans'], queryFn: () => api.adminAppLibraryPlans() });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const kept = apps.filter((a) => {
      if (needle && !a.name.toLowerCase().includes(needle) && !a.packageName.toLowerCase().includes(needle)) return false;
      if (fShelf !== 'all' && a.availability !== fShelf) return false;
      if (fDist !== 'all' && a.distribution !== fDist) return false;
      if (fPlan !== 'all') {
        const all = a.offeredPackageIds.length === 0;      // empty list = every plan
        if (!(all || a.offeredPackageIds.includes(fPlan))) return false;
      }
      if (fState !== 'all') {
        const st = newest(a)?.state ?? null;
        if (fState === 'none' ? st !== null : st !== fState) return false;
      }
      return true;
    });
    if (!sort) return kept.sort((a, b) => attention(a) - attention(b) || a.name.localeCompare(b.name));
    const dir = sort.dir === 'asc' ? 1 : -1;
    return kept.sort((a, b) => (compare(a, b, sort.key) * dir) || a.name.localeCompare(b.name));
  }, [apps, q, sort, fState, fShelf, fDist, fPlan]);
  const totals = useMemo(() => ({
    apps: apps.length,
    review: apps.reduce((n, a) => n + a.versions.filter((v) => v.state === 'review').length, 0),
    testing: apps.reduce((n, a) => n + a.versions.filter((v) => v.state === 'testing').length, 0),
    behind: apps.reduce((n, a) => n + a.fleet.behind, 0),
    failing: apps.reduce((n, a) => n + a.fleet.failing, 0),
  }), [apps]);
  const filtered = fState !== 'all' || fShelf !== 'all' || fDist !== 'all' || fPlan !== 'all';
  const sel = apps.find((a) => a.id === selected) ?? null;
  const sortBy = (key: SortKey) => setSort((cur) => (cur?.key === key
    ? (cur.dir === 'asc' ? { key, dir: 'desc' as const } : null)   // asc → desc → back to attention order
    : { key, dir: 'asc' as const }));

  return (
    // At 2xl the page is exactly the viewport: the panes scroll, the page does not.
    // position: sticky cannot help here — the admin shell is min-h-screen, so the
    // window scrolls while the content box does not, and a sticky child of a
    // scroll container that never scrolls simply rides up and out of sight.
    <div className="space-y-6 2xl:h-[calc(100vh-7rem)] 2xl:flex 2xl:flex-col 2xl:space-y-4">
      <div className="flex items-start justify-between gap-4 2xl:flex-none">
        <div>
          <h1 className="text-2xl font-bold text-white">App Library</h1>
          <p className="text-sm text-slate-400 mt-1">Third-party apps approved for Scouts. A Scout only ever sees versions marked Released for its plan; Tester Scouts also see Testing.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => refetch()} className="inline-flex items-center gap-1 rounded-md border border-slate-600 px-3 py-2 text-sm text-slate-300"><ArrowPathIcon className="h-4 w-4" /> Refresh</button>
          <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded-md bg-cyan-600 px-3 py-2 text-sm font-semibold text-white"><PlusIcon className="h-4 w-4" /> Add app</button>
        </div>
      </div>

      {/* One line, not four cards. This page is worked in: the figures are a glance
          on the way to an app, and every row they add is a row someone scrolls past. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-slate-700 bg-slate-800/60 px-4 py-2.5 text-sm 2xl:flex-none">
        {[
          { v: totals.apps, l: 'in the library' },
          { v: totals.review, l: 'waiting for review', warn: totals.review > 0 },
          { v: totals.testing, l: 'in testing' },
          { v: totals.behind, l: 'Scouts behind', warn: totals.behind > 0 },
          { v: totals.failing, l: 'installs stuck', warn: totals.failing > 0 },
        ].map((k) => (
          <span key={k.l} className="flex items-baseline gap-1.5">
            <span className={`text-lg font-bold ${k.warn ? 'text-amber-300' : 'text-white'}`}>{k.v}</span>
            <span className="text-slate-400">{k.l}</span>
          </span>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-400 2xl:flex-none">
        <input className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white w-64" placeholder="Search apps or packages…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="rounded-md border border-slate-700 bg-slate-800 px-2.5 py-2 text-sm text-white" value={fState} onChange={(e) => setFState(e.target.value as typeof fState)}>
          <option value="all">Any state</option>
          {(['draft', 'review', 'testing', 'released', 'archived'] as State[]).map((st) => <option key={st} value={st}>{st[0].toUpperCase() + st.slice(1)}</option>)}
          <option value="none">No versions</option>
        </select>
        <select className="rounded-md border border-slate-700 bg-slate-800 px-2.5 py-2 text-sm text-white" value={fShelf} onChange={(e) => setFShelf(e.target.value as typeof fShelf)}>
          <option value="all">Any shelf</option>
          {SHELVES.map((sh) => <option key={sh.value} value={sh.value}>{sh.label}</option>)}
        </select>
        <select className="rounded-md border border-slate-700 bg-slate-800 px-2.5 py-2 text-sm text-white" value={fDist} onChange={(e) => setFDist(e.target.value as typeof fDist)}>
          <option value="all">Any distribution</option>
          <option value="library">Vortex serves the APK</option>
          <option value="skipper_managed">Skipper-managed</option>
        </select>
        <select className="rounded-md border border-slate-700 bg-slate-800 px-2.5 py-2 text-sm text-white" value={fPlan} onChange={(e) => setFPlan(e.target.value)}>
          <option value="all">Any plan</option>
          {plans.filter((p) => p.visibility !== 'hidden' || p.name.trim().startsWith('Tester')).map((p) => <option key={p.id} value={p.id}>{p.name.trim()}</option>)}
        </select>
        {(filtered || sort) && (
          <button onClick={() => { setFState('all'); setFShelf('all'); setFDist('all'); setFPlan('all'); setSort(null); }} className="rounded-md border border-slate-600 px-2.5 py-2 text-sm text-slate-300 hover:text-white">Clear</button>
        )}
        <span className="ml-auto whitespace-nowrap">
          {rows.length === apps.length ? `${apps.length} apps` : `${rows.length} of ${apps.length} apps`}
          {!sort && ' · sorted by attention needed'}
        </span>
      </div>

      {isError && <div className="rounded-md border border-red-800/60 bg-red-900/20 px-3 py-2 text-sm text-red-300">Could not load the library.</div>}

      {/* The editor sits BESIDE the list and stays put while the list scrolls.
          Picking an app used to mean scrolling past every other app to reach its
          settings, then scrolling back up to pick the next one. */}
      <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_minmax(560px,42%)] gap-5 items-start 2xl:flex-1 2xl:min-h-0">
      <div className="overflow-x-auto rounded-xl border border-slate-700 2xl:h-full 2xl:overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 text-xs uppercase tracking-wider text-slate-400 sticky top-0 z-10">
            <tr>
              <SortHeader label="App" k="name" sort={sort} onSort={sortBy} />
              <SortHeader label="Newest" k="newest" sort={sort} onSort={sortBy} />
              <SortHeader label="State" k="state" sort={sort} onSort={sortBy} />
              <SortHeader label="On the Scout" k="availability" sort={sort} onSort={sortBy} />
              <SortHeader label="Fleet" k="fleet" sort={sort} onSort={sortBy} />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/60">
            {isLoading && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">{apps.length === 0 ? 'No apps yet. Add the first one, then drop its APK set.' : 'No apps match the search and filters.'}</td></tr>}
            {rows.map((a) => {
              const rel = released(a); const nw = newest(a);
              const state: State | null = nw?.state ?? null;
              return (
                <tr key={a.id} onClick={() => setSelected(selected === a.id ? null : a.id)} className={`cursor-pointer hover:bg-slate-800/60 ${selected === a.id ? 'bg-cyan-900/20' : ''}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-white leading-tight">{a.name}</div>
                    <div className="text-xs text-slate-500 font-mono leading-tight">{a.packageName}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-200">
                    {nw ? nw.versionName : <span className="text-slate-600">—</span>}
                    {rel && nw && rel.versionCode !== nw.versionCode && <div className="text-xs text-slate-500 font-sans">released {rel.versionName}</div>}
                  </td>
                  <td className="px-3 py-2">{a.distribution === 'skipper_managed' ? <span className="inline-flex rounded-full bg-slate-700/60 px-2 py-0.5 text-xs text-slate-300">Skipper-managed</span> : state ? <StateChip state={state} /> : <span className="text-xs text-slate-600">no versions</span>}</td>
                  <td className="px-3 py-2"><ShelfChip availability={a.availability} /></td>
                  <td className="px-3 py-2 w-28"><FleetBar fleet={a.fleet} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* key={sel.id} is load-bearing: the settings fields are uncontrolled, so
          without it React keeps the previous app's values — Erchang Sonar showed
          Navionics' tile image, and a blur would have saved it onto the wrong app. */}
      <div className="2xl:h-full 2xl:min-h-0 2xl:overflow-y-auto">
        {sel
          ? <AppDetail key={sel.id} app={sel} plans={plans} onClose={() => setSelected(null)} />
          : (
            <div className="rounded-xl border border-dashed border-slate-700 px-5 py-12 text-center text-sm text-slate-500">
              Pick an app on the left to see its versions and settings.
            </div>
          )}
      </div>
      </div>

      {adding && <AddAppModal apps={apps} plans={plans} onClose={() => setAdding(false)} />}
      {!isLoading && apps.length > 0 && <div className="text-xs text-slate-500 flex items-center gap-1"><CheckCircleIcon className="h-4 w-4" /> Scouts verify each file's sha256 after download; a set that fails never installs.</div>}
    </div>
  );
}
